import * as React from "react";
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    XAxis,
    YAxis,
} from "recharts";

import { cn } from "../../utils/utils";
import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from "../ui/chart";

export interface AreaTrendSeries {
    /** Chave da série no objeto de dados (= dataKey). Deve existir no `config`. */
    key: string;
    /** "area" (preenchimento com gradiente) ou "line" (ex.: média/referência). Default: "area". */
    type?: "area" | "line";
    /** Linha tracejada — útil para séries de referência. */
    dashed?: boolean;
    /** Textura de listras diagonais sobre o gradiente (só p/ "area"). Default: true. */
    stripes?: boolean;
}

interface AreaTrendChartProps {
    data: Array<Record<string, unknown>>;
    /** Rótulos + cores das séries. A cor vira `--color-<key>` (consumida pelo gradiente/traço). */
    config: ChartConfig;
    /** Chave do eixo X. */
    xKey: string;
    series: AreaTrendSeries[];
    /** Exibe o eixo Y (default: false — visual mais limpo). */
    showYAxis?: boolean;
    /** Exibe a legenda (default: false — desnecessária em série única). */
    showLegend?: boolean;
    xTickFormatter?: (value: string) => string;
    labelFormatter?: (value: string) => string;
    className?: string;
}

/**
 * Gráfico de tendência (área + linhas de referência) padronizado sobre os primitivos
 * `ui/chart`. Cores vêm do `config` (temáveis por CSS var), nunca hardcoded.
 */
export function AreaTrendChart({
    data,
    config,
    xKey,
    series,
    showYAxis = false,
    showLegend = false,
    xTickFormatter,
    labelFormatter,
    className,
}: AreaTrendChartProps) {
    const areaSeries = series.filter((s) => (s.type ?? "area") === "area");

    return (
        <ChartContainer config={config} className={cn("aspect-auto h-full w-full", className)}>
            <ComposedChart data={data} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                <defs>
                    {areaSeries.map((s) => (
                        <React.Fragment key={s.key}>
                            <linearGradient id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={`var(--color-${s.key})`} stopOpacity={0.5} />
                                <stop offset="95%" stopColor={`var(--color-${s.key})`} stopOpacity={0.05} />
                            </linearGradient>
                            <pattern
                                id={`stripe-${s.key}`}
                                patternUnits="userSpaceOnUse"
                                width={6}
                                height={6}
                            >
                                <path
                                    d="M0,6 L6,0"
                                    stroke={`var(--color-${s.key})`}
                                    strokeWidth={0.8}
                                    opacity={0.15}
                                />
                            </pattern>
                        </React.Fragment>
                    ))}
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
                <XAxis
                    dataKey={xKey}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={24}
                    tickFormatter={xTickFormatter}
                />
                {showYAxis && (
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} width={32} />
                )}
                <ChartTooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={<ChartTooltipContent indicator="dot" labelFormatter={labelFormatter} />}
                />
                {showLegend && <ChartLegend content={<ChartLegendContent />} />}
                {series.map((s) => {
                    if ((s.type ?? "area") === "line") {
                        return (
                            <Line
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                stroke={`var(--color-${s.key})`}
                                strokeWidth={2}
                                strokeDasharray={s.dashed ? "5 5" : undefined}
                                dot={false}
                            />
                        );
                    }
                    return (
                        <React.Fragment key={s.key}>
                            <Area
                                type="monotone"
                                dataKey={s.key}
                                stroke={`var(--color-${s.key})`}
                                strokeWidth={2}
                                fill={`url(#fill-${s.key})`}
                                fillOpacity={1}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            {(s.stripes ?? true) && (
                                <Area
                                    type="monotone"
                                    dataKey={s.key}
                                    stroke="transparent"
                                    fill={`url(#stripe-${s.key})`}
                                    fillOpacity={1}
                                    dot={false}
                                    activeDot={false}
                                    legendType="none"
                                    tooltipType="none"
                                    isAnimationActive={false}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </ComposedChart>
        </ChartContainer>
    );
}
